import React from "react";
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Flex,
  message,
  Spin,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "../../../services/react-query/queryKeys";
import { addBrand, showBrand, updateBrand } from "../../../network/products";
import { useIntl } from "react-intl";
import { checkStringInLanguage } from "../../../utils/utils";

type addBarndProps = {
  isOpen: boolean;
  brandId?: number | null;
  handelModalClose: () => void;
  setBrandId: (id: number) => void;
};
export default function AddBrand({
  brandId,
  isOpen,
  handelModalClose,
  setBrandId,
}: addBarndProps) {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const { data: brandDetails, isLoading: brandDetailsLoading } = useQuery(
    [queryKeys.listBrands, brandId],
    () => {
      return showBrand(brandId, true);
    },
    {
      select: (res) => res?.data,
      enabled: brandId ? true : false,
    }
  );
  const { mutate: onAddBrand, isLoading } = useMutation(addBrand, {
    onSuccess: (res: any) => {
      if (res?.data?.data?.id) {
        setBrandId(res?.data?.data?.id);
      }
      message.success(intl.formatMessage({ id: "brandAddedSuccess" }));
      handelModalClose();
      queryClient.invalidateQueries([queryKeys.listBrands]);
    },
    onError: (error: any) => {
      message.error("حاول مرة اخرى");
    },
  });
  const { mutate: onUpdateBrand, isLoading: updateBrandLoading } = useMutation(
    (body: { name: { en: string; ar: string } }) => {
      return updateBrand(brandId, body);
    },
    {
      onSuccess: (res: any) => {
        if (res?.data?.data?.id) {
          setBrandId(res?.data?.data?.id);
        }
        message.success(intl.formatMessage({ id: "brandUpdatedSuccess" }));
        handelModalClose();
        queryClient.invalidateQueries([queryKeys.listBrands]);
      },
      onError: (error: any) => {
        message.error("حاول مرة اخرى");
      },
    }
  );

  const handleAddBrand = (values: any) => {
    const body = {
      name: {
        ar: values?.name?.ar,
        en: values?.name?.en,
      },
    } as any;
    if (brandId) {
      onUpdateBrand(body);
    } else {
      onAddBrand(body);
    }
  };

  return (
    <Modal
      width={500}
      title={intl.formatMessage({ id: "addBrand" })}
      open={isOpen}
      onCancel={() => handelModalClose()}
      footer={null}
      destroyOnClose
    >
      {brandId && brandDetailsLoading ? (
        <div className="flex justify-center items-center h-full w-full">
          <Spin size="large" />
        </div>
      ) : (
        <Form
          onFinish={handleAddBrand}
          layout="vertical"
          initialValues={brandDetails}
        >
          <Row gutter={{ xs: 8, sm: 16, md: 8, lg: 14, xl: 30 }}>
            <Col xl={24} lg={24} xs={24} sm={24}>
              <Form.Item
                name={["name", "ar"]}
                label={
                  intl.formatMessage({ id: "brandName" }) +
                  " " +
                  intl.formatMessage({ id: "inArabic" })
                }
                required={false}
                className="form-style w-full"
                rules={[
                  { required: true },
                  {
                    type: "string",
                    min: 3,
                    message: intl.formatMessage({ id: "nameLt3Chr" }),
                  },
                  {
                    validator: (_, value) => {
                      if (value) {
                        if (checkStringInLanguage(value, "ar")) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            new Error(
                              intl.formatMessage({
                                id: "service_name_arabic_error",
                              })
                            )
                          );
                        }
                      }
                    },
                  },
                ]}
              >
                <Input
                  placeholder={
                    intl.formatMessage({ id: "brandName" }) +
                    " " +
                    intl.formatMessage({ id: "inArabic" })
                  }
                />
              </Form.Item>
            </Col>
            <Col xl={24} lg={24} xs={24} sm={24}>
              <Form.Item
                name={["name", "en"]}
                label={
                  intl.formatMessage({ id: "brandName" }) +
                  " " +
                  intl.formatMessage({ id: "inEnglish" })
                }
                required={false}
                className="form-style w-full"
                rules={[
                  { required: true },
                  {
                    type: "string",
                    min: 3,
                    message: intl.formatMessage({ id: "nameLt3Chr" }),
                  },
                  {
                    validator: (_, value) => {
                      if (value) {
                        if (checkStringInLanguage(value, "en")) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            new Error(
                              intl.formatMessage({
                                id: "service_name_english_error",
                              })
                            )
                          );
                        }
                      }
                    },
                  },
                ]}
              >
                <Input
                  placeholder={
                    intl.formatMessage({ id: "brandName" }) +
                    " " +
                    intl.formatMessage({ id: "inEnglish" })
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Flex gap="small" align="center" justify="center">
            <Button
              type="primary"
              loading={isLoading || updateBrandLoading}
              block
              htmlType="submit"
            >
              {intl.formatMessage({ id: brandId ? "edit" : "add" })}
            </Button>
            <Button
              type="primary"
              block
              onClick={() => handelModalClose()}
              ghost
            >
              {intl.formatMessage({ id: "cancel" })}
            </Button>
          </Flex>
        </Form>
      )}
    </Modal>
  );
}
